<template>
  <div class="space-y-2">
    <h4 class="text-sm font-medium text-gray-700">Follow-up Actions</h4>
    
    <div class="flex flex-wrap gap-2">
      <!-- Generate follow-up button -->
      <button
        v-if="canGenerateFollowUp"
        @click="handleGenerateFollowUp"
        :disabled="actionInProgress"
        class="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
      >
        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
        </svg>
        Generate Follow-up
      </button>
      
      <!-- Skip follow-up button -->
      <button 
        v-if="canSkipFollowUp"
        @click="handleSkipFollowUp"
        :disabled="actionInProgress"
        class="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
      >
        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
        Skip Follow-up
      </button>
      
      <!-- Reschedule follow-up button -->
      <button 
        v-if="canRescheduleFollowUp"
        @click="showRescheduleModal"
        :disabled="actionInProgress"
        class="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
      >
        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        Reschedule
      </button>
    </div>
    
    <!-- Success message -->
    <div v-if="successMessage" class="rounded-md bg-green-50 p-2 mt-2">
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-4 w-4 text-green-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-2">
          <p class="text-xs font-medium text-green-800">
            {{ successMessage }}
          </p>
        </div>
      </div>
    </div>
    
    <!-- Error message -->
    <div v-if="errorMessage" class="rounded-md bg-red-50 p-2 mt-2">
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-4 w-4 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-2">
          <p class="text-xs font-medium text-red-800">
            {{ errorMessage }}
          </p>
        </div>
      </div>
    </div>
    
    <!-- Reschedule Modal -->
    <div v-if="showReschedule" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 class="text-lg font-medium text-gray-900 mb-4">Reschedule Follow-up</h3>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Follow-up Date</label>
          <input 
            type="date" 
            v-model="rescheduledDate" 
            class="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>
        <div class="flex justify-end space-x-3">
          <button 
            @click="showReschedule = false"
            class="px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button 
            @click="handleRescheduleFollowUp"
            :disabled="actionInProgress || !rescheduledDate"
            class="px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
          >
            Reschedule
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useFollowUps } from '@/composables/useFollowUps'

const props = defineProps<{
  emailId: string
  isFollowUpEmail: boolean
  followUpCount: number
  hasReplies: boolean
  status: string
}>()

const emit = defineEmits<{
  (e: 'follow-up-action-completed'): void
}>()

const { 
  actionInProgress,
  generateFollowUp,
  skipFollowUp,
  rescheduleFollowUp
} = useFollowUps()

const successMessage = ref('')
const errorMessage = ref('')
const showReschedule = ref(false)
const rescheduledDate = ref('')

// Computed properties to determine which actions are available
const canGenerateFollowUp = computed(() => {
  // Can generate follow-up if:
  // 1. Not already a follow-up email
  // 2. Status is "sent"
  // 3. Follow-up count is less than 3 (max 3 follow-ups)
  return !props.isFollowUpEmail && 
         props.status === 'sent' && 
         props.followUpCount < 3 && 
         !props.hasReplies
})

const canSkipFollowUp = computed(() => {
  // Can skip follow-up if:
  // 1. Not already a follow-up email
  // 2. Status is "sent"
  // 3. The email has no replies
  return !props.isFollowUpEmail && 
         props.status === 'sent' && 
         !props.hasReplies
})

const canRescheduleFollowUp = computed(() => {
  // Can reschedule follow-up if:
  // 1. Not already a follow-up email
  // 2. Status is "sent"
  // 3. The email has no replies
  return !props.isFollowUpEmail && 
         props.status === 'sent' && 
         !props.hasReplies
})

async function handleGenerateFollowUp() {
  successMessage.value = ''
  errorMessage.value = ''
  
  try {
    await generateFollowUp(props.emailId)
    successMessage.value = 'Follow-up email generated successfully'
    setTimeout(() => {
      successMessage.value = ''
    }, 3000)
    emit('follow-up-action-completed')
  } catch (err: any) {
    errorMessage.value = err || 'Failed to generate follow-up'
    setTimeout(() => {
      errorMessage.value = ''
    }, 5000)
  }
}

async function handleSkipFollowUp() {
  if (!confirm('Are you sure you want to skip this follow-up?')) return
  
  successMessage.value = ''
  errorMessage.value = ''
  
  try {
    await skipFollowUp(props.emailId)
    successMessage.value = 'Follow-up skipped successfully'
    setTimeout(() => {
      successMessage.value = ''
    }, 3000)
    emit('follow-up-action-completed')
  } catch (err: any) {
    errorMessage.value = err || 'Failed to skip follow-up'
    setTimeout(() => {
      errorMessage.value = ''
    }, 5000)
  }
}

function showRescheduleModal() {
  // Set default date to tomorrow
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  rescheduledDate.value = tomorrow.toISOString().split('T')[0]
  showReschedule.value = true
}

async function handleRescheduleFollowUp() {
  if (!rescheduledDate.value) return
  
  successMessage.value = ''
  errorMessage.value = ''
  
  try {
    await rescheduleFollowUp(props.emailId, new Date(rescheduledDate.value).toISOString())
    successMessage.value = 'Follow-up rescheduled successfully'
    setTimeout(() => {
      successMessage.value = ''
    }, 3000)
    showReschedule.value = false
    rescheduledDate.value = ''
    emit('follow-up-action-completed')
  } catch (err: any) {
    errorMessage.value = err || 'Failed to reschedule follow-up'
    setTimeout(() => {
      errorMessage.value = ''
    }, 5000)
  }
}
</script> 